import { usePublicMetadata } from '../seo/PublicMetadata.jsx';
import { useInitialPublicData } from '../seo/PublicBootstrap.jsx';
import React, { useEffect, useMemo, useState } from 'react';
import { Check, Minus } from 'lucide-react';
import { Badge, Button } from '@mts/ui';
import {
  DEFAULT_DIRECTORY_PRICING_CONTENT,
  DIRECTORY_LISTING_TIERS,
  DIRECTORY_PRICING_COMPARISON,
  DIRECTORY_PRICING_FAQS,
  DIRECTORY_PRICING_VALUE_BLOCKS,
} from '@mts/shared/directoryPricing';
import { usePublicTenant } from '../hooks/usePublicTenant';
import { fetchPublicListingPlans } from '../services/api';
import { buildPublicPageMetadata } from '../utils/publicMetadata.js';
import { buildBreadcrumbSchema, buildWebPageSchema } from '../utils/structuredData.js';

function cleanString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function getSettingValue(tenant, key) {
  const list = Array.isArray(tenant?.tenantSettings) ? tenant.tenantSettings : [];
  return list.find((item) => item?.settingKey === key)?.settingValue;
}

function resolveSiteName({ tenant, spotlightName }) {
  return (
    cleanString(tenant?.websiteContact?.publicDisplayName) ||
    cleanString(spotlightName) ||
    cleanString(getSettingValue(tenant, 'tenant.name')) ||
    cleanString(tenant?.name) ||
    'My Town Spotlight'
  );
}

function resolvePricingContent(tenant) {
  const pageContent = tenant?.websitePages?.pricing?.content;
  if (!pageContent || typeof pageContent !== 'object') {
    return DEFAULT_DIRECTORY_PRICING_CONTENT;
  }

  return {
    hero: pageContent.hero || DEFAULT_DIRECTORY_PRICING_CONTENT.hero,
    valueBlocks: Array.isArray(pageContent.valueBlocks) ? pageContent.valueBlocks : DIRECTORY_PRICING_VALUE_BLOCKS,
    tiers: Array.isArray(pageContent.tiers) ? pageContent.tiers : DIRECTORY_LISTING_TIERS,
    comparison: Array.isArray(pageContent.comparison) ? pageContent.comparison : DIRECTORY_PRICING_COMPARISON,
    faqs: Array.isArray(pageContent.faqs) ? pageContent.faqs : DIRECTORY_PRICING_FAQS,
    meta: pageContent.meta || DEFAULT_DIRECTORY_PRICING_CONTENT.meta,
  };
}

function renderComparisonValue(value) {
  if (value === true) {
    return <Check className="mx-auto h-5 w-5 text-primary" aria-label="Included" />;
  }
  if (value === false || value === null || value === undefined || value === '') {
    return <Minus className="mx-auto h-5 w-5 text-text-secondary/50" aria-label="Not included" />;
  }
  return <span>{value}</span>;
}

function PricingCard({ tier }) {
  const isFeatured = tier.key === 'featured';

  return (
    <article
      className={[
        'flex h-full flex-col rounded-lg border bg-[var(--card,#FFFFFF)] p-6 shadow-sm',
        isFeatured ? 'border-primary shadow-md ring-2 ring-primary/20' : 'border-border',
      ].join(' ')}
    >
      <div className="flex min-h-[32px] items-center justify-between gap-3">
        <h2 className="text-2xl font-bold text-text-primary">{tier.name}</h2>
        {tier.badge ? <Badge variant={isFeatured ? 'primary' : 'secondary'}>{tier.badge}</Badge> : null}
      </div>
      <div className="mt-5 flex items-end gap-1">
        <span className="text-4xl font-bold text-text-primary">{tier.price}</span>
        {tier.priceSubtext ? <span className="pb-1 text-base font-semibold text-text-secondary">{tier.priceSubtext}</span> : null}
      </div>
      <p className="mt-4 min-h-[76px] text-sm leading-6 text-text-secondary">{tier.description}</p>
      <Button asChild variant={isFeatured ? 'primary' : 'outline'} size="lg" className="mt-6 w-full rounded-md">
        <a href={tier.ctaHref}>{tier.ctaLabel}</a>
      </Button>
      <ul className="mt-6 space-y-3 text-sm text-text-secondary">
        {tier.features.map((feature) => (
          <li key={feature} className="flex gap-3">
            <Check className="mt-0.5 h-4 w-4 flex-none text-primary" aria-hidden="true" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function formatPlanPrice(option) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: option.currency || 'USD', maximumFractionDigits: 2,
  }).format(Number(option.amountCents || 0) / 100);
}

function buildPaidTier(tierKey, option) {
  if (!option) return null;
  const params = new URLSearchParams({
    plan: tierKey,
    billing: option.period,
    productId: option.productId,
    priceId: option.priceId,
  });
  return {
    key: tierKey,
    name: option.name || (tierKey === 'spotlight' ? 'Spotlight' : 'Featured'),
    price: formatPlanPrice(option),
    priceSubtext: option.billingInterval === 'YEAR' ? '/yr' : '/mo',
    badge: tierKey === 'featured' ? 'Most Popular' : 'Best Visibility',
    description: option.description || '',
    ctaLabel: tierKey === 'spotlight' ? 'Become a Spotlight Partner' : 'Become Featured',
    ctaHref: `/claim/new?${params.toString()}`,
    features: Array.isArray(option.features) ? option.features : [],
  };
}

function buildComparison(tiers) {
  const rows = [];
  const seen = new Set();
  for (const tier of tiers) {
    for (const feature of tier.features || []) {
      const key = String(feature).trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      rows.push({
        feature,
        ...Object.fromEntries(tiers.map((candidate) => [candidate.key, (candidate.features || []).some((item) => String(item).trim().toLowerCase() === key)])),
      });
    }
  }
  return rows;
}

function DirectoryPricingPage() {
  const { tenant, spotlightName, tenantLogoConfig, tenantLogoImageUrl } = usePublicTenant();
  const siteName = useMemo(() => resolveSiteName({ tenant, spotlightName }), [tenant, spotlightName]);
  const content = useMemo(() => resolvePricingContent(tenant), [tenant]);
  const initial = useInitialPublicData("pricing");
  const [catalog, setCatalog] = useState(initial || []);
  const [catalogStatus, setCatalogStatus] = useState(initial ? 'ready' : 'loading');
  const [billingPeriod, setBillingPeriod] = useState(() => initial && !['featured', 'spotlight'].every(key => initial.find(tier => tier.tierKey === key)?.options?.some(option => option.period === 'month')) && ['featured', 'spotlight'].every(key => initial.find(tier => tier.tierKey === key)?.options?.some(option => option.period === 'year')) ? 'year' : 'month');
  const basicTier = content.tiers.find((tier) => tier?.key === 'basic') || DIRECTORY_LISTING_TIERS.find((tier) => tier.key === 'basic');

  useEffect(() => {
    if (initial) return;
    let active = true;
    fetchPublicListingPlans().then((payload) => {
      if (!active) return;
      const next = Array.isArray(payload?.tiers) ? payload.tiers : [];
      setCatalog(next);
      const commonPeriods = ['month', 'year'].filter((period) => ['featured', 'spotlight'].every((key) => next.find((tier) => tier.tierKey === key)?.options?.some((option) => option.period === period)));
      setBillingPeriod(commonPeriods.includes('month') ? 'month' : commonPeriods[0] || 'month');
      setCatalogStatus('ready');
    }).catch(() => {
      if (active) setCatalogStatus('error');
    });
    return () => { active = false; };
  }, [initial]);

  const paidTiers = useMemo(() => ['featured', 'spotlight'].map((tierKey) => {
    const tier = catalog.find((item) => item.tierKey === tierKey);
    return buildPaidTier(tierKey, tier?.options?.find((option) => option.period === billingPeriod));
  }).filter(Boolean), [billingPeriod, catalog]);
  const tiers = [basicTier, ...paidTiers];
  const comparison = useMemo(() => buildComparison(tiers), [tiers]);
  const availablePeriods = ['month', 'year'].filter((period) => ['featured', 'spotlight'].every((key) => catalog.find((tier) => tier.tierKey === key)?.options?.some((option) => option.period === period)));

  usePublicMetadata(() => {
    const title = cleanString(tenant?.websitePages?.pricing?.seoTitle) || cleanString(content.meta?.title) || `Directory Listing Pricing | ${siteName}`;
    const description =
      cleanString(tenant?.websitePages?.pricing?.seoDescription) ||
      cleanString(content.meta?.description) ||
      'Choose a directory listing plan to help local customers find and contact your business.';

    if (!tenant) return;
    const resolvedTitle = title.includes('My Town Spotlight') ? title.replace('My Town Spotlight', siteName) : title;
    const metadata = buildPublicPageMetadata({ tenant, pathname: '/advertise/spotlight', canonicalPath: '/advertise/spotlight', siteName, cityName: tenantLogoConfig?.cityName, logoUrl: tenantLogoImageUrl, title: resolvedTitle, description });
    metadata.jsonLd = [buildWebPageSchema({ url: metadata.canonicalUrl, name: resolvedTitle, description }), buildBreadcrumbSchema({ url: metadata.canonicalUrl, items: [{ name: 'Home', url: tenant.canonicalOrigin }, { name: 'Advertise Your Business', url: metadata.canonicalUrl }] })];
    return (metadata);
  }, [content.meta?.description, content.meta?.title, siteName, tenant, tenant?.websitePages?.pricing?.seoDescription, tenant?.websitePages?.pricing?.seoTitle, tenantLogoConfig?.cityName, tenantLogoImageUrl]);

  return (
    <div className="bg-surface text-text-primary">
      <section className="border-b border-border bg-surface">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12">
          <p className="text-sm font-semibold uppercase tracking-wider text-primary">{content.hero.eyebrow}</p>
          <h1 className="mt-3 max-w-4xl text-4xl font-bold leading-tight text-text-primary sm:text-5xl">
            {content.hero.title}
          </h1>
          <p className="mt-4 max-w-3xl text-lg leading-8 text-text-secondary">{content.hero.description}</p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Button asChild variant="primary" size="lg" className="rounded-md">
              <a href={content.hero.primaryCta.href}>{content.hero.primaryCta.label}</a>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-md">
              <a href={content.hero.secondaryCta.href}>{content.hero.secondaryCta.label}</a>
            </Button>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-surface">
        <div className="mx-auto grid max-w-6xl gap-4 px-4 py-6 sm:px-6 md:grid-cols-3 lg:px-8">
          {content.valueBlocks.map((item) => (
            <div key={item.title} className="rounded-lg border border-border bg-[var(--card,#FFFFFF)] p-5 shadow-sm">
              <h2 className="text-base font-semibold text-text-primary">{item.title}</h2>
              <p className="mt-2 text-sm leading-6 text-text-secondary">{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="compare-plans" className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12">
        <div className="mb-6 flex justify-center" role="group" aria-label="Billing period">
          {['month', 'year'].map((period) => (
            <button key={period} type="button" disabled={!availablePeriods.includes(period)} aria-pressed={billingPeriod === period} onClick={() => setBillingPeriod(period)} className={`border px-4 py-2 text-sm font-semibold first:rounded-l-md last:rounded-r-md ${billingPeriod === period ? 'bg-primary text-primary-content' : 'bg-[var(--card,#fff)] text-text-primary'} disabled:cursor-not-allowed disabled:opacity-40`}>
              {period === 'month' ? 'Monthly' : 'Annual'}
            </button>
          ))}
        </div>
        <div className="grid gap-5 lg:grid-cols-3">
          {catalogStatus === 'loading' ? <><div className="h-96 animate-pulse rounded-lg bg-border/40" /><div className="h-96 animate-pulse rounded-lg bg-border/40" /><div className="h-96 animate-pulse rounded-lg bg-border/40" /></> : tiers.map((tier) => (
            <PricingCard key={tier.key} tier={tier} />
          ))}
        </div>

        {catalogStatus !== 'loading' && paidTiers.length < 2 ? <p className="mt-6 rounded-md border border-border bg-[var(--card,#fff)] p-4 text-center text-sm text-text-secondary">Paid plans are temporarily unavailable. You can still claim a free Basic listing.</p> : null}

        {catalogStatus !== 'loading' && comparison.length ? <div className="mt-10 overflow-x-auto rounded-lg border border-border bg-[var(--card,#FFFFFF)] shadow-sm">
          <table className="min-w-[720px] w-full border-collapse text-left text-sm">
            <thead className="bg-surface text-text-primary">
              <tr>
                <th className="px-4 py-4 font-semibold">Feature</th>
                {tiers.map((tier) => (
                  <th key={tier.key} className="px-4 py-4 text-center font-semibold">{tier.name}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {comparison.map((row) => (
                <tr key={row.feature}>
                  <th className="px-4 py-4 font-medium text-text-primary">{row.feature}</th>
                  {tiers.map((tier) => (
                    <td key={tier.key} className="px-4 py-4 text-center text-text-secondary">
                      {renderComparisonValue(row[tier.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div> : null}
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-10 sm:px-6 lg:px-8 lg:pb-12">
        <h2 className="text-3xl font-bold text-text-primary">Questions businesses ask</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {content.faqs.map((item) => (
            <article key={item.question} className="rounded-lg border border-border bg-[var(--card,#FFFFFF)] p-5 shadow-sm">
              <h3 className="text-base font-semibold text-text-primary">{item.question}</h3>
              <p className="mt-2 text-sm leading-6 text-text-secondary">{item.answer}</p>
            </article>
          ))}
        </div>
        <div className="mt-8 rounded-lg border border-border bg-[var(--card,#FFFFFF)] p-6 shadow-sm">
          <h2 className="text-2xl font-bold text-text-primary">Ready to make your business easier to find?</h2>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-text-secondary">
            Start with a free listing or choose a visibility plan that helps local customers notice and contact you.
          </p>
          <Button asChild variant="primary" size="lg" className="mt-5 rounded-md">
            <a href="/claim/new?plan=basic">Claim Free Listing</a>
          </Button>
        </div>
      </section>
    </div>
  );
}

export default DirectoryPricingPage;
